import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import UpgradeLock, { useSubscriptionAccess } from "@/components/UpgradeLock";
import { useChatHistory } from "@/hooks/useChatHistory";
import useIsDesktop from "@/hooks/useIsDesktop";
// iter592 — DashboardGuide import премахнат (бутонът „Ръководство" е премахнат)

// Core components (loaded immediately)
import DashboardHeader from "@/components/DashboardHeader";
import ProfileCompletenessBanner from "@/components/ProfileCompletenessBanner";
import SystemHealthBanner from "@/components/SystemHealthBanner";  // iter708 — patient-facing system status pill
import OverviewCards from "@/components/OverviewCards";
import DailyTracking from "@/components/DailyTracking";
import MealLog from "@/components/MealLog";
import MealHistoryPanel from "@/components/MealHistoryPanel";
import MobileBottomNav from "@/components/MobileBottomNav";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";
import RegulatoryFooter from "@/components/common/RegulatoryFooter";  // iter561
import BCSubtypeOnboardingPrompt from "@/components/onboarding/BCSubtypeOnboardingPrompt";  // iter1018
import ScreenRecorderWidget from "@/components/ScreenRecorderWidget";  // iter1023
import AdaptiveHeroCard from "@/dashboard/AdaptiveHeroCard";  // iter563
import StarterTilesGrid from "@/dashboard/StarterTilesGrid";  // iter563
import OnboardingWizard from "@/dashboard/OnboardingWizard";  // iter564 — 3-step first-visit wizard
import DilHeroCard from "@/dashboard/DilHeroCard";  // iter568 — Big DIL KPI hero (Phase 8)
import UnifiedHealthTwinCard from "@/components/UnifiedHealthTwinCard";  // iter603
import { useDashboardMode } from "@/dashboard/DashboardModeContext";  // iter563
// VoiceLogFab премахнат iter350.D — оставяме import коментиран за бъдещо
// re-enable, но не го рендираме на Dashboard.
// import VoiceLogFab from "@/components/VoiceLogFab";

// Lazy-loaded components (loaded on demand)
const MetabolicPanel = lazy(() => import("@/components/MetabolicPanel"));
const MealPlanner = lazy(() => import("@/components/MealPlanner"));
const WeeklyChart = lazy(() => import("@/components/WeeklyChart"));
const HealthIndices = lazy(() => import("@/components/HealthIndices"));
const HealthCalculators = lazy(() => import("@/components/HealthCalculators"));
const LabUploadAnalyzer = lazy(() => import("@/components/LabUploadAnalyzer"));
const DrugFoodInteractions = lazy(() => import("@/components/DrugFoodInteractions"));
const ChatInterface = lazy(() => import("@/components/ChatInterface"));
const AIInsightsPanel = lazy(() => import("@/components/AIInsightsPanel"));
const ChatHistoryList = lazy(() => import("@/components/ChatHistoryList"));
const HistoryPanel = lazy(() => import("@/components/HistoryPanel"));
const ProfileEditModal = lazy(() => import("@/components/ProfileEditModal"));
const ExportPanel = lazy(() => import("@/components/ExportPanel"));
const TrialDashboard = lazy(() => import("@/components/TrialDashboard"));
const TodaysActionCard = lazy(() => import("@/components/TodaysActionCard"));  // iter488
const LifestyleStreakBadge = lazy(() => import("@/components/LifestyleStreakBadge"));  // iter489
const HealthHub = lazy(() => import("@/components/dashboard/HealthHub"));  // iter490 — Premium precision-health hub
const ProgramsPanel = lazy(() => import("@/components/ProgramsPanel"));
const WeeklyReport = lazy(() => import("@/components/WeeklyReport"));
const VeganChallengeCard = lazy(() => import("@/components/VeganChallengeCard"));
const UpgradeNudge = lazy(() => import("@/components/UpgradeNudge"));
const PwaInstallPrompt = lazy(() => import("@/components/PwaInstallPrompt"));
import ScrollToTop from "@/components/ScrollToTop";
import GuidedTour from "@/components/GuidedTour";  // iter686 — DISABLED; replaced by ModuleShortcutTiles
import PinnedTilesRow from "@/components/dashboard/PinnedTilesRow";  // iter998 — закачени плочки
import AnalysesCarousel from "@/components/AnalysesCarousel";
const AIRecommendationPopup = lazy(() => import("@/components/AIRecommendationPopup"));
const RecipeExplorer = lazy(() => import("@/components/RecipeExplorer"));
const Nutrigenomics = lazy(() => import("@/components/Nutrigenomics"));
const ClinicalAnalysis = lazy(() => import("@/components/ClinicalAnalysis"));
const DentalRiskTile = lazy(() => import("@/components/DentalRiskTile"));
const CortisolIntelTile = lazy(() => import("@/components/CortisolIntelTile"));
const TwinLiveTile = lazy(() => import("@/components/TwinLiveTile"));
import OnboardingChecklist from "@/components/OnboardingChecklist";
import PlanUsageCard from "@/components/PlanUsageCard";
import NextStepCard from "@/components/NextStepCard";
import MetabolicStatusHeroCard from "@/components/MetabolicStatusHeroCard";
import CompositePhenotypeCard from "@/components/CompositePhenotypeCard";
import DashboardGreeting from "@/components/dashboard/DashboardGreeting";
import DashboardRightPanel from "@/components/dashboard/DashboardRightPanel";
import HealthCalculatorsResultsCard from "@/components/dashboard/HealthCalculatorsResultsCard";
import DailyInsightCard from "@/components/DailyInsightCard";
import DailyRecommendationCard from "@/components/DailyRecommendationCard";
import DilBudgetMeter from "@/components/DilBudgetMeter";
import MyDataCompletenessStrip from "@/components/dashboard/MyDataCompletenessStrip";

// Mobile compact variant of the DIL meter. Small wrapper so we can re-style
// without pulling in the full DilBudgetMeter in-place.
const DilBudgetMeterMobile = () => <DilBudgetMeter compact />;
import LockedValueCard from "@/components/LockedValueCard";
import ProfileCompletenessCard from "@/components/ProfileCompletenessCard";
import LabUploadReTrigger from "@/components/LabUploadReTrigger";
import PanelLockOverlay from "@/components/PanelLockOverlay";
import SmartNotifications from "@/components/SmartNotifications";
import SmartUpgradeSuggestion from "@/components/SmartUpgradeSuggestion";
import InboxPanel from "@/components/InboxPanel";
import FeedbackPanel from "@/components/FeedbackPanel";
import ReviewPromptModal from "@/components/ReviewPromptModal";  // iter1114
import ReferralPanel from "@/components/ReferralPanel";
import MoreDrawer from "@/components/MoreDrawer";
import MealPlanReview from "@/components/MealPlanReview";
import MedicalImageScreening from "@/components/MedicalImageScreening";
import BiologicalAgePanel from "@/components/BiologicalAgePanel";
import LongevityScorePanel from "@/components/LongevityScorePanel";
import NephrologyPanel from "@/components/NephrologyPanel";
import PhenotypeClassifier from "@/components/PhenotypeClassifier";
import NutrientDeficiencyPanel from "@/components/NutrientDeficiencyPanel";
import DietaryPatternsCard from "@/components/DietaryPatternsCard";
import DemoExamplesPanel from "@/components/DemoExamplesPanel";
import DailyDiaryNote from "@/components/DailyDiaryNote";
import BackfillNudge from "@/components/BackfillNudge";
import PathwayCoverageCard from "@/components/PathwayCoverageCard";
import MealComplianceWidget from "@/components/MealComplianceWidget";
import MealTimingOptimizer from "@/components/MealTimingOptimizer";
import MindBodyRecommender from "@/components/MindBodyRecommender";
import SymptomTrackerWidget from "@/components/SymptomTrackerWidget";
import ComplianceConsentModal from "@/components/compliance/ComplianceConsentModal";
import SecuritySettings from "@/components/SecuritySettings";
import { useCreditGate } from "@/components/CreditGateContext";
import DashboardWidget from "@/dashboard/DashboardWidget";  // iter549 customizable widgets
const MealIntelligence = lazy(() => import("@/components/MealIntelligence"));
const FoodImageAnalyzer = lazy(() => import("@/components/FoodImageAnalyzer"));
const ClinicianPatientsPanel = lazy(() => import("@/components/ClinicianPatientsPanel"));
const PlateSimulator = lazy(() => import("@/components/PlateSimulator"));
const DigitalTwinPanel = lazy(() => import("@/components/DigitalTwinPanel"));
const VisceralAdiposityCard = lazy(() => import("@/components/VisceralAdiposityCard"));
const VAISReductionPathwayPanel = lazy(() => import("@/components/VAISReductionPathwayPanel"));
const VAISTeaserCard = lazy(() => import("@/components/VAISTeaserCard"));
const MealsActionGrid = lazy(() => import("@/components/MealsActionGrid"));
const VoiceLogFab = lazy(() => import("@/components/VoiceLogFab"));
const BarcodeScannerModal = lazy(() => import("@/components/meal-log/BarcodeScannerModal"));
const ProUpsellNudge = lazy(() => import("@/components/ProUpsellNudge"));
const DigitalTwinFoundationCard = lazy(() => import("@/components/twin/DigitalTwinFoundationCard"));
const PhenotypeSignatureCard = lazy(() => import("@/components/twin/PhenotypeSignatureCard"));
const EpigeneticMapPanel = lazy(() => import("@/components/EpigeneticMapPanel"));
const RecipeVisionUpload = lazy(() => import("@/components/RecipeVisionUpload"));
// iter502 — Interactive onboarding tour (Levels/Oura/ZOE-style)
// iter592 — InteractiveTour import премахнат („Започни тук" floating chip е премахнат)
// const InteractiveTour = lazy(() => import("@/components/dashboard/InteractiveTour"));
// iter511 — Mobile-first Health Hub view (Oura/Levels/Zoe/InsideTracker mix)
const MobileHealthHubView = lazy(() => import("@/components/dashboard/MobileHealthHubView"));
import MobileFoldSection from "@/components/dashboard/MobileFoldSection";
import AdaptiveGroup from "@/components/dashboard/AdaptiveGroup";  // iter1076
import RepeatYesterdayCard from "@/components/meal-log/RepeatYesterdayCard";  // iter1076
import TodayVerdictCard from "@/components/dashboard/TodayVerdictCard";  // iter1081
import { useViewPrefs } from "@/lib/useViewPrefs";  // iter1073
import { ViewCustomizeBar, HideableSection } from "@/components/shared/ViewCustomizeBar";  // iter1073
import MobileScrollProgress from "@/components/dashboard/MobileScrollProgress";
import AppPreferencesPanel from "@/components/dashboard/AppPreferencesPanel";
import NisGaugeCard from "@/components/dashboard/NisGauge";  // iter586 — NIS exposure
import CircadianWindow from "@/components/dashboard/CircadianWindow";  // iter586
import LivePulse from "@/components/dashboard/LivePulse";  // iter586 — „жив организъм"
import TodayInterventionCard from "@/components/dashboard/TodayInterventionCard";  // iter587 — жива верига
import { Utensils, Activity, Sparkles, Camera as CameraIcon, LayoutGrid, HeartPulse as HeartPulseIcon, ChevronRight as ChevronRightIcon, BookOpen } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import UniversalTooltip from "@/components/common/UniversalTooltip";

/* iter591 — Command Center компонентите са изнесени в отделни файлове
   под @/components/dashboard/ (преди бяха ~1400 реда inline тук).
   SectionHeader, ModulePortalCard и AdvancedCollapsible бяха неизползвани
   и са изтрити. */
import ModulePortalLive from "@/components/dashboard/ModulePortalLive";
import HiScoreHero from "@/components/dashboard/HiScoreHero";
import TwinScoreHero from "@/components/dashboard/TwinScoreHero";  // iter614
import InsulinBudgetMiniCard from "@/components/dashboard/InsulinBudgetMiniCard";
import QuickActionsRow from "@/components/dashboard/QuickActionsRow";
import DailyFocusCard from "@/components/dashboard/DailyFocusCard";
import WeeklyGoalsCard from "@/components/goals/WeeklyGoalsCard";
import DailyVitalsTile from "@/components/dashboard/DailyVitalsTile";
import PlateTrendChart from "@/components/plate/PlateTrendChart";  // iter1071
import LabAggregateNudgeCard from "@/components/dashboard/LabAggregateNudgeCard";  // iter692
// iter696: DigitalTwin3D + R3FMinimal premature — r3f 9.x + React 19 + CRA babel
// incompatibility (R3F: Cannot set "x-line-number"). Component file preserved за
// бъдеща iter697 миграция към Vite или ProductionConfig wrapper.
import { AttentionNeededCard, HealthJourneyCard, WelcomeStateCard, RecentChangesCard } from "@/components/dashboard/CommandCenterStatusCards";
import LastAiReportCard from "@/components/dashboard/LastAiReportCard";
import DashboardDocumentUploadCard from "@/components/dashboard/DashboardDocumentUploadCard";  // iter894
import UnifiedDocumentUpload from "@/components/breast_intel/UnifiedDocumentUpload";  // iter1011 — общ прозорец за качване
import GraduateToExpertPrompt from "@/components/dashboard/GraduateToExpertPrompt";  // iter975
import ReleaseNotesFooter from "@/components/dashboard/ReleaseNotesFooter";  // iter977
import AutoDiagnosisPanel from "@/components/breast_intel/AutoDiagnosisPanel";  // iter920 — cross-system pattern-recognition panel
// iter912 — BhiPreviewCard + DailyCheckinTile removed from Dashboard.
// Both remain reachable via left-nav (/bhi, /pathway-vault).
import PassiveStressBanner from "@/components/dashboard/PassiveStressBanner";

export default function Dashboard() {
  const { user, refreshUser, API } = useAuth();
  const { isEasy: isEasyMode, mode } = useDashboardMode();  // iter563 — gate Hero+Tiles; iter573 — mode for KPI strip gating
  const { requireCredits } = useCreditGate();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const navigate = useNavigate();
  // Conditional-render guard: mount само 1× вариант (desktop ИЛИ mobile) за да
  // не дублираме API calls от двата layout-а. Tailwind `md:` = ≥768px.
  const isDesktop = useIsDesktop(768);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const { history: chatHistory, addEntry: addChatEntry, removeEntry: removeChatEntry, clearHistory: clearChatHistory } = useChatHistory();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePanel, setActivePanel] = useState("insights");
  const [moreDrawer, setMoreDrawer] = useState(null); // "inbox" | "weekly" | "progress" | "recipes" | "referral" | null
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [weeklyData, setWeeklyData] = useState([]);
  const [dilRefreshKey, setDilRefreshKey] = useState(0);
  const [mealDilData, setMealDilData] = useState({ daily_effective_dil: 0, daily_raw_dil: 0 });

  // iter924 — Consume pre-populated query from BHI "Обсъди с [Bot]" deep-link.
  // Fallback order: URL ?ai_query= → sessionStorage prefilled_ai_query.
  // iter927 — Extracted the prefill logic into a reusable function so BOTH the
  // mount effect AND the same-page `dashboard:prefill-ai-chat` event can use it.
  const applyPrefillToChat = useCallback((preQuery) => {
    if (!preQuery) return;
    setQuery(preQuery);
    try {
      sessionStorage.removeItem("prefilled_ai_query");
      sessionStorage.removeItem("prefilled_ai_bot");
    } catch (e) { /* ignore */ }
    // Retry-scroll: ChatInterface is React.lazy — poll for its
    // appearance every 300ms up to 6s, then scroll + focus once.
    let attempts = 0;
    const tryScroll = () => {
      attempts += 1;
      const el = document.querySelector('[data-testid="chat-interface"]');
      const inp = document.querySelector('[data-testid="query-input"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        if (inp && inp.focus) inp.focus();
        return;
      }
      if (attempts < 20) setTimeout(tryScroll, 300);
    };
    setTimeout(tryScroll, 400);
    toast.info("Въпросът е предварително попълнен от вашия AI отчет. Натиснете 'Попитай AI' за да продължите.");
  }, []);

  useEffect(() => {
    try {
      let pre = "";
      try {
        const usp = new URLSearchParams(window.location.search);
        pre = usp.get("ai_query") || "";
      } catch (e) { /* URL parse failed */ }
      if (!pre) {
        pre = sessionStorage.getItem("prefilled_ai_query") || "";
      } else {
        // If URL provided the query, prefer the FULL sessionStorage version
        // when it exists (URL only carries a short compact form).
        const full = sessionStorage.getItem("prefilled_ai_query");
        if (full) pre = full;
      }
      if (pre) applyPrefillToChat(pre);
    } catch (err) { /* sessionStorage unavailable */ }
  }, [applyPrefillToChat]);

  // iter927 — Same-page prefill event from AutoDiagnosisPanel "Discuss with Bot".
  // When user is already on Dashboard, we can't rely on the mount effect above;
  // this listener drives the prefill without a route navigation.
  useEffect(() => {
    const onPrefill = (e) => {
      const q = e?.detail?.query || "";
      if (q) applyPrefillToChat(q);
    };
    window.addEventListener("dashboard:prefill-ai-chat", onPrefill);
    return () => window.removeEventListener("dashboard:prefill-ai-chat", onPrefill);
  }, [applyPrefillToChat]);

  // iter1011 — общ прозорец за качване: всяка плочка може да го отвори
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadHint, setUploadHint] = useState("");
  useEffect(() => {
    const onOpen = (e) => {
      setUploadHint(e?.detail?.hint || "");
      setUploadOpen(true);
    };
    window.addEventListener("app:upload:open", onOpen);
    return () => window.removeEventListener("app:upload:open", onOpen);
  }, []);


  const handleStartChatFromGene = (prompt) => {
    setQuery(prompt);
    // Scroll to chat after a small delay to let state update
    setTimeout(() => {
      const chatEl = document.querySelector('[data-testid="chat-interface"]');
      if (chatEl) {
        chatEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const textarea = chatEl.querySelector('textarea');
        if (textarea) textarea.focus();
      }
    }, 200);
  };
  // Mobile section state — 4 tabs: meals, health, ai (Анализи), more
  const [mobileSection, setMobileSection] = useState("myday");
  // iter1073 — кои секции на таб „Хранене" потребителят е скрил
  const mealPrefs = useViewPrefs("meals");
  // iter1076 — същото за таб „Моят ден" (частите му са сгъваеми на телефон)
  const mydayPrefs = useViewPrefs("myday");
  // iter974 — Simple/Expert view toggle. Persisted per-user in
  // localStorage so a returning patient sees the layout they chose
  // last time. `basic` shows only 3 essential cards (Diary link,
  // Insulin block, BHI CTA); `expert` renders the full command
  // centre. Newly-registered users start in `basic` — they can grow
  // into the full-featured view when they're ready.
  const [viewMode, setViewModeState] = useState(() => {
    try {
      const stored = localStorage.getItem("dashboard-view-mode");
      return stored === "expert" || stored === "basic" ? stored : "basic";
    } catch {
      return "basic";
    }
  });
  const setViewMode = (m) => {
    setViewModeState(m);
    try {
      localStorage.setItem("dashboard-view-mode", m);
    } catch {}
  };
  const isBasicView = viewMode === "basic";
  // Desktop main column tab state — 3 tabs: daily / nutrition / body (right sidebar persists)
  const [desktopTab, setDesktopTab] = useState(() => {
    if (typeof window === "undefined") return "daily";
    try { return localStorage.getItem("dashboard-desktop-tab") || "daily"; } catch { return "daily"; }
  });
  useEffect(() => {
    try { localStorage.setItem("dashboard-desktop-tab", desktopTab); } catch {}
  }, [desktopTab]);
  const [mealLogOpenSignal, setMealLogOpenSignal] = useState(0);
  const [trackingDate, setTrackingDate] = useState(new Date().toISOString().slice(0, 10));

  // iter230 — "Try with my data" demo intent handler.
  // The landing page demo modals save a key to localStorage and route to /register.
  // After the user finishes registration + onboarding and lands here, we route
  // them straight to the relevant feature so the WOW moment continues.
  useEffect(() => {
    let intent;
    try { intent = localStorage.getItem("nutramilo_demo_intent"); } catch { intent = null; }
    if (!intent) return;
    try { localStorage.removeItem("nutramilo_demo_intent"); } catch {}
    const messages = {
      "digital-twin": bg ? "🎬 Digital Twin demo сценарий — добавете supplement-и и натиснете Симулирай" : "🎬 Digital Twin demo loaded — add supplements and tap Simulate",
      "plate":        bg ? "🎬 Plate Simulator demo — добавете храни в чинията за live AI оценки" : "🎬 Plate Simulator demo loaded — add foods for live AI scoring",
      "epigenetic":   bg ? "🎬 Епигенетична карта demo — изберете заболяване от списъка" : "🎬 Epigenetic Map demo loaded — pick a disease from the list",
    };
    const msg = messages[intent];
    if (!msg) return;
    if (intent === "epigenetic") {
      toast.success(msg, { duration: 6000 });
      setTimeout(() => navigate("/dashboard/analyses?tab=epimap&demo=1"), 250);
    } else {
      // digital-twin and plate both live on /dashboard/diary
      toast.success(msg, { duration: 6000 });
      setTimeout(() => navigate(`/dashboard/diary?demo=${intent}`), 250);
    }
  }, [bg, navigate]);

  // Global listener so cross-component CTAs (DailyInsight, NextStep, LabUpload,
  // Voice logger, etc.) can force-open the Meal Log panel and auto-scroll to it.
  // Without this, the collapsible panel stays collapsed and the scroll looks
  // broken to the user.
  useEffect(() => {
    const openMealLog = () => {
      setMealLogOpenSignal(s => s + 1);
      // iter327X — на mobile MealLog е вътре в MobileFoldSection.
      // Expand-ваме fold-а чрез localStorage (key="mobile-meallog") + scroll.
      try { localStorage.setItem("foldsection.mobile-meallog", "1"); } catch {}
      setTimeout(() => {
        const el = document.getElementById("meal-log-anchor")
          || document.querySelector('[data-testid="mobile-fold-meallog"]')
          || document.querySelector('[data-testid="meal-log-card"]')
          || document.querySelector('[data-testid="meal-log-card-m"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    };
    // iter327X — bridge PlateSimulator/DigitalTwin "Add food" event to mobile MealLog fold.
    // iter327Z — запомняме scroll позицията преди да отворим търсачката, за да върнем
    // потребителя на чинията след като приключи (вместо да остане на дъното при MealLog).
    let savedScrollY = 0;
    const focusSearch = () => {
      savedScrollY = window.scrollY;
      try { sessionStorage.setItem("nutramilo_addfood_return_y", String(savedScrollY)); } catch {}
      try { localStorage.setItem("foldsection.mobile-meallog", "1"); } catch {}
      setMealLogOpenSignal(s => s + 1);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="mobile-fold-meallog"]')
          || document.querySelector('[data-testid="meal-log-card"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    };
    // When MealLog closes its search overlay, scroll back to where user was.
    const restoreScroll = () => {
      try {
        const y = parseInt(sessionStorage.getItem("nutramilo_addfood_return_y") || "0", 10);
        if (y > 0) {
          window.scrollTo({ top: y, behavior: "smooth" });
          sessionStorage.removeItem("nutramilo_addfood_return_y");
        }
      } catch {}
    };
    window.addEventListener("dashboard:open-meal-log", openMealLog);
    window.addEventListener("meal-log:focus-search", focusSearch);
    window.addEventListener("meal-log:search-closed", restoreScroll);
    window.addEventListener("meal-added", restoreScroll);

    // Cross-tab calculator opener — DailyTracking lives in Анализи but the
    // health calculators (water/fiber/sleep) live in Здраве. This bridges them.
    const openCalc = (e) => {
      const testId = e?.detail?.testId;
      if (!testId) return;
      // iter327Z — Health calculators преместени в Анализи tab.
      // На desktop панелът е винаги в grid-а, така че директният DOM lookup
      // ще успее.
      setMobileSection("ai");
      let attempts = 0;
      const tryClick = () => {
        const el = document.querySelector(`[data-testid="${testId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => el.click(), 350);
          return;
        }
        if (++attempts < 10) setTimeout(tryClick, 150);
      };
      setTimeout(tryClick, 100);
    };
    window.addEventListener("dashboard:open-calc", openCalc);

    return () => {
      window.removeEventListener("dashboard:open-meal-log", openMealLog);
      window.removeEventListener("meal-log:focus-search", focusSearch);
      window.removeEventListener("meal-log:search-closed", restoreScroll);
      window.removeEventListener("meal-added", restoreScroll);
      window.removeEventListener("dashboard:open-calc", openCalc);
    };
  }, []);

  const needsOnboarding = user && user.profile?.completedOnboarding !== true;
  const { isFree, canClinicalPhenotypes } = useSubscriptionAccess();
  const isClinician = user?.role === "clinician" && user?.clinician_status === "approved";

  const [dailyData, setDailyData] = useState({
    calories: 0, protein: 0, carbs: 0, fat: 0,
    water: 0, sleep: 0, activity: 0
  });

  const profile = user?.profile;
  const goals = profile?.goals || {
    calories: 2000, protein: 150, carbs: 200, fat: 65,
    water: 8, sleep: 8, activity: 30
  };

  const fetchDailyTracking = useCallback(async (dateParam) => {
    try {
      const d = dateParam || trackingDate;
      const { data } = await axios.get(`${API}/tracking/daily?date=${d}`);
      if (data.data) setDailyData(data.data);
      else setDailyData({ calories: 0, protein: 0, carbs: 0, fat: 0, water: 0, sleep: 0, activity: 0 });
    } catch (e) {
      // silent
    }
  }, [API, trackingDate]);

  const fetchWeeklyData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tracking/weekly`);
      setWeeklyData(data.weekly || []);
    } catch (e) {
      // silent
    }
  }, [API]);

  const fetchMealDilData = useCallback(async () => {
    try {
      const [mealsRes, dilRes] = await Promise.all([
        axios.get(`${API}/meals/today`).catch(() => ({ data: {} })),
        axios.get(`${API}/dil/today`).catch(() => ({ data: {} })),
      ]);
      // iter586 — NIS exposure: последното хранене по created_at + дневна статистика.
      // Числата идват готови от backend (/meals/today → nis_by_meal, nis_daily).
      const mealsObj = mealsRes.data.meals || {};
      let lastMeal = null;
      Object.entries(mealsObj).forEach(([type, arr]) => (arr || []).forEach((e) => {
        if (!lastMeal || String(e.created_at) > String(lastMeal.created_at)) lastMeal = { ...e, meal_type: type };
      }));
      const nisByMeal = mealsRes.data.nis_by_meal || {};
      setMealDilData({
        daily_effective_dil: mealsRes.data.daily_effective_dil || 0,
        daily_raw_dil: mealsRes.data.daily_raw_dil || 0,
        // Endo Score от backend — същият, който MetabolicPanel използва.
        // Това синхронизира таблото с метаболитния панел.
        endo_score: dilRes.data?.endo_score || null,
        nis_daily: mealsRes.data.nis_daily || null,
        meal_count: Object.values(mealsObj).reduce((n, arr) => n + (arr?.length || 0), 0),
        last_meal_type: lastMeal?.meal_type || null,
        last_meal_name: lastMeal?.food_name || null,
        last_meal_nis: lastMeal ? (nisByMeal[lastMeal.meal_type] || null) : null,
      });
    } catch (e) {
      // silent
    }
  }, [API]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/query/history`);
      setHistory(data.queries || []);
    } catch (e) {
      // silent
    }
  }, [API]);

  // iter586 — час на последна тиха синхронизация („жив организъм")
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // iter575 — HI summary + DIL suggestions за Command Center hero + budget
  const [hiSummary, setHiSummary] = useState(null);
  const [dilBudget, setDilBudget] = useState(null); // personalised ceiling
  const fetchHiSummary = useCallback(async () => {
    try {
      const [hi, sug] = await Promise.all([
        axios.get(`${API}/health-intelligence/summary?window_days=30`).catch(() => null),
        axios.get(`${API}/dil/suggestions`).catch(() => null),
      ]);
      setHiSummary(hi?.data || null);
      setDilBudget(Number(sug?.data?.daily_dil_budget) || null);
    } catch (e) {
      // silent
    }
  }, [API]);

  useEffect(() => {
    fetchHistory();
    fetchDailyTracking();
    fetchWeeklyData();
    fetchMealDilData();
    fetchHiSummary();
    setLastSyncAt(new Date());
  }, [fetchHistory, fetchDailyTracking, fetchWeeklyData, fetchMealDilData, fetchHiSummary]);

  // iter586 — „Жив организъм": тихо опресняване на метаболитния образ на
  // всеки 60 сек. (само при видим таб) + веднага при връщане към таба.
  // Без видимо мигане — само LivePulse редът обновява часа си.
  useEffect(() => {
    const silentRefresh = () => {
      fetchDailyTracking();
      fetchMealDilData();
      fetchHiSummary();
      setLastSyncAt(new Date());
    };
    const id = setInterval(() => {
      if (document.visibilityState === "visible") silentRefresh();
    }, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") silentRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [fetchDailyTracking, fetchMealDilData, fetchHiSummary]);

  // Ослушване за meal-added event от QuickLogModal (OverviewCards)
  // и други компоненти които не използват callback-а onMealAdded.
  useEffect(() => {
    const refresh = () => {
      fetchDailyTracking();
      fetchWeeklyData();
      fetchMealDilData();
      setDilRefreshKey(k => k + 1);
    };
    window.addEventListener("meal-added", refresh);
    window.addEventListener("meal-log-changed", refresh);
    // Feb 2026 — Body metrics update triggers DIL/HI recalculation.
    // BodyMetricsEditModal dispatches this event after successful PUT
    // /api/user/profile with weight/height/waist/hip/neck.
    const refreshOnBodyMetrics = () => {
      fetchDailyTracking();
      fetchMealDilData();
      fetchHiSummary();
      setDilRefreshKey(k => k + 1);
    };
    window.addEventListener("nutramilo:body-metrics-updated", refreshOnBodyMetrics);
    return () => {
      window.removeEventListener("meal-added", refresh);
      window.removeEventListener("meal-log-changed", refresh);
      window.removeEventListener("nutramilo:body-metrics-updated", refreshOnBodyMetrics);
    };
  }, [fetchDailyTracking, fetchWeeklyData, fetchMealDilData]);

  // iter588 — микро-сигнал: какво се промени в метаболитния образ между
  // две опреснявания (напр. „+1 хранене · DIL 63.5→71.2"). Сравняваме
  // снимка на ключовите показатели при всяка промяна на данните.
  const prevSnapRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const [liveChange, setLiveChange] = useState(null);
  useEffect(() => {
    // Предпазител: първите секунди след зареждане данните пристигат на
    // порции (хранения, проследяване, скор) — без него първоначалното
    // зареждане се обявява като фалшива „промяна".
    const warmedUp = Date.now() - mountedAtRef.current > 8000;
    const snap = {
      dil: Math.round((mealDilData?.daily_effective_dil ?? 0) * 10) / 10,
      meals: mealDilData?.meal_count ?? 0,
      water: dailyData?.water ?? 0,
      activity: dailyData?.activity ?? 0,
      sleep: dailyData?.sleep ?? 0,
      hi: hiSummary?.hi_score?.value ?? null,
    };
    const prev = prevSnapRef.current;
    prevSnapRef.current = snap;
    if (!prev || !warmedUp) return;
    const parts = [];
    if (snap.meals > prev.meals) parts.push(bg ? `+${snap.meals - prev.meals} хранене` : `+${snap.meals - prev.meals} meal`);
    if (snap.dil !== prev.dil) parts.push(`DIL ${prev.dil}→${snap.dil}`);
    if (snap.water > prev.water) parts.push(bg ? `+${snap.water - prev.water} чаша вода` : `+${snap.water - prev.water} glass water`);
    if (snap.activity > prev.activity) parts.push(bg ? `+${snap.activity - prev.activity} мин движение` : `+${snap.activity - prev.activity} min movement`);
    if (snap.sleep !== prev.sleep && snap.sleep > 0) parts.push(bg ? `сън ${snap.sleep} ч` : `sleep ${snap.sleep} h`);
    if (snap.hi != null && prev.hi != null && snap.hi !== prev.hi) parts.push(bg ? `показател ${prev.hi}→${snap.hi}` : `index ${prev.hi}→${snap.hi}`);
    if (parts.length) setLiveChange({ text: parts.slice(0, 3).join(" · "), at: Date.now() });
  }, [mealDilData, dailyData, hiSummary, bg]);


  const handleAskAI = async () => {
    if (!query.trim()) {
      toast.error(t('common.error'));
      return;
    }
    setLoading(true);
    const submittedQuery = query.trim();
    try {
      const { data } = await axios.post(`${API}/query/execute`, { query, language });
      setResult(data);
      if (data?.final_response) {
        addChatEntry(submittedQuery, data);
      }
      await refreshUser();
      await fetchHistory();
      toast.success(t('common.success'));
    } catch (e) {
      if (e.response?.status === 402) {
        requireCredits(bg ? "AI анализ" : "AI analysis", e.response?.data?.detail?.match(/Required: (\d+)/)?.[1] || 1);
      } else {
        toast.error(e.response?.data?.detail || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (item) => {
    setQuery(item.query);
    setResult({
      ...item.result,
      agents_used: item.result.agents_used,
      estimated_credits: item.result.credits_used
    });
    setActivePanel("insights");
  };

  const updateDailyData = async (key, value) => {
    const newValue = Math.max(0, value);
    setDailyData(prev => ({ ...prev, [key]: newValue }));
    try {
      await axios.post(`${API}/tracking/daily`, { key, value: newValue });
      fetchWeeklyData();
    } catch (e) {
      // silent
    }
  };

  const handleDateChange = (newDate) => {
    setTrackingDate(newDate);
    fetchDailyTracking(newDate);
  };

  const handleAddFromFoodAnalysis = async (macros) => {
    const updates = [
      { key: "calories", value: dailyData.calories + (macros.calories || 0) },
      { key: "protein", value: dailyData.protein + (macros.protein || 0) },
      { key: "carbs", value: dailyData.carbs + (macros.carbs || 0) },
      { key: "fat", value: dailyData.fat + (macros.fat || 0) },
    ];
    for (const u of updates) {
      await updateDailyData(u.key, u.value);
    }
    fetchDailyTracking();
    setDilRefreshKey(k => k + 1);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-background' : 'bg-[#FAFBFC]'}`}>
      <SEO title={bg ? "Моето здраве" : "My Health"} noindex />
      {/* iter1011 — всяко „Качи изследване" на екрана отваря този прозорец */}
      <UnifiedDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang="bg"
        initialHint={uploadHint}
      />
      {/* Top thin reminder banner removed (Apr 26 2026) — keep only the
          larger „Попълнете здравната оценка" card below to avoid duplicate
          prompts. */}

      {isFree && user?.role !== "admin" && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2.5 px-4" data-testid="free-tier-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-sm text-center sm:text-left">
              {bg ? "Безплатен план — AI функциите са заключени. Надградете за пълен достъп." : "Free plan — AI features are locked. Upgrade for full access."}
            </p>
            <button 
              onClick={() => navigate('/upgrade')}
              data-testid="upgrade-from-banner-btn"
              className="px-4 py-1.5 bg-white text-amber-600 rounded-lg text-sm font-medium hover:bg-white/90 whitespace-nowrap"
            >
              {bg ? "Надградете" : "Upgrade"}
            </button>
          </div>
        </div>
      )}

      <DashboardHeader 
        activePanel={activePanel} 
        setActivePanel={setActivePanel}
        onEditProfile={() => setShowProfileEdit(true)}
        onOpenMore={(id) => setMoreDrawer(id)}
      />
      {/* Mobile: compact DIL budget meter sticky under header (само мобилно;
          desktop използва AppShell DIL meter за да избегнем дублиране). */}
      <div className={`md:hidden sticky top-16 z-30 px-3 py-2 border-b ${isDark ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}`} data-testid="dil-meter-mobile">
        <DilBudgetMeterMobile />
      </div>
      
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6 pb-24 md:pb-6">
        {/* iter1089 — Профил completeness banner, автоматично скрит при ≥80% попълнен. */}
        <ProfileCompletenessBanner />

        {/* iter734 — SystemHealthBanner скрит по искане на потребителя.
            Компонентът остава жив за evtl. вътрешна употреба (admin/status). */}
        {/* <SystemHealthBanner lang={language || "bg"} /> */}

        {/* iter564 — Auto-opens for first-visit users; replaces old 7-step
            DashboardGuide tutorial. Renders for ALL modes (onboarding should
            reach every new user regardless of disclosure preference). */}
        <OnboardingWizard />

        {/* iter575 — Easy/Standard/Expert унифицирани в единичен Command Center.
            Старите AdaptiveHero + StarterTiles + DilHero за Easy премахнати —
            единен опит за всички потребители (виж блока по-долу на desktop).
            Mobile запазва съществуващото поведение (MobileHealthHubView). */}

        {needsOnboarding && (
          <div className="mb-4 rounded-2xl p-4 sm:p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-900/50" data-testid="health-assessment-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg">⚕️</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-bold text-amber-900 dark:text-amber-200 truncate">
                  {user?.profile?.completedOnboarding ? "" : "Попълнете здравната оценка"}
                </h3>
                <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300/80">
                  Отключва всички здравни показатели, DIL план и AI препоръки. ~3 минути.
                </p>
              </div>
              <a
                href="/onboarding"
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white text-xs sm:text-sm font-semibold shadow-md hover:shadow-lg transition-shadow flex-shrink-0"
                data-testid="health-assessment-banner-cta"
              >
                Почни →
              </a>
            </div>
          </div>
        )}
        {/* iter575 — Премахнат !isEasyMode гейт; режимите вече не съществуват
            на UI ниво (DashboardModeContext остава вътрешно като default). */}
        <Suspense fallback={null}>
        {/* iter592 — InteractiveTour „Започни тук" премахнат по user request */}
        {/* iter575 — DashboardGreeting премахнат от desktop (новият HiScoreHero
            го замества с inline greeting + HI score). Mobile продължава да
            ползва DashboardHeader greeting. */}

        {/* iter511 — Premium mobile Health Hub hero (Oura/Levels/Zoe inspired)
            Renders FIRST on mobile across all sections. */}
        {!isDesktop && mobileSection !== "myday" && (
          <Suspense fallback={<div className="h-[320px] rounded-3xl bg-slate-100 dark:bg-slate-900/60 animate-pulse mb-4" />}>
            <div className="mb-4">
              <MobileHealthHubView showModules={mobileSection !== "meals"} />
            </div>
          </Suspense>
        )}

        {/* iter380 — Mobile-only: Action grid FIRST on Хранене tab so the
            patient hits tools immediately. Desktop пропуска това (има sidebar). */}
        {!isDesktop && mobileSection === "meals" && (
          <div className="mb-4">
            <Suspense fallback={<div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />}>
              <MealsActionGrid />
            </Suspense>
          </div>
        )}

        {/* iter382 — Pro upsell nudge for Core users at ≥80% usage.
            Component self-hides if user is not Core or below threshold. */}
        {(isDesktop || mobileSection === "meals") && (
          <div className="mb-4">
            <Suspense fallback={null}>
              <ProUpsellNudge />
            </Suspense>
          </div>
        )}

        {/* Retention Hero — top-of-dashboard metabolic status (synced с trackingDate, #16 fix).
            Mobile (iter327X): показваме само в Хранене tab — потребителят не иска
            cardа да заема място на Здраве/Анализи tab. */}
        {(isDesktop || mobileSection === "meals" || mobileSection === "myday") && (
          <div className="mb-4" data-testid="dashboard-hero-wrap">
            <DashboardWidget id="metabolic-hero">
              <MetabolicStatusHeroCard selectedDate={trackingDate} refreshKey={dilRefreshKey} />
            </DashboardWidget>
          </div>
        )}

        {/* iter501 — CompositePhenotypeCard преместен ВЪТРЕ в HealthHub
            като реален "Днешен приоритет" slot за desktop.
            На mobile (където HealthHub не се рендира) пазим standalone. */}
        {/* iter1073 — „Как реагира тялото в момента" е премахната от таб
            „Хранене" по заявка на потребителя (когнитивно претоварване).
            Картата остава на таб „Моето здраве" и на едър екран. */}

        {/* iter377 — VAIS Iteration B (Висцерален адипозен индекс).
            iter380: на Dashboard показваме само компактен TEASER. Пълната
            карта + Reduction Pathway са в /analyses?tab=clinical#vais
            (по заявка на потребителя — за да не доминира главния dashboard).
            MDR: educational only, not a medical device. */}
        {/* iter575 — VAISTeaserCard скрит на desktop (вече е достъпен през
            Health Intelligence portal). На mobile (health tab) остава. */}
        {!isDesktop && mobileSection === "health" && (
          <div className="mb-4" data-testid="dashboard-vais-wrap">
            <Suspense fallback={<div className="h-20 rounded-2xl bg-slate-100 animate-pulse" />}>
              <VAISTeaserCard refreshKey={dilRefreshKey} />
            </Suspense>
          </div>
        )}

        {/* iter575 — UNIFIED HEALTH COMMAND CENTER (desktop only).
            Изградено по wireframe-а от потребителя:
              Block 1 — HI Hero (greeting + score 71/100 + delta)
              Block 2 — 2 cards (Insulin Budget + Today Intervention)
              Block 3 — Attention Needed (missing data bullets)
              Block 4 — 3 module portals row #1 (Nutrition/MindBody/Precision)
              Block 5 — 3 module portals row #2 (Digital Twin/Organ Intel/AI)
              Block 6 — Recent Changes (4 ✓ items)
            Mobile секцията (по-долу) НЕ е променяна. */}
        {/* iter590 — Командното табло се рендира на desktop И на mobile в таб „Моят ден" */}
        {(isDesktop || mobileSection === "myday") && (
        <div className="mt-6 space-y-5" data-testid="dashboard-command-center">

          {/* iter974 — Basic / Expert view toggle. Top-right, discrete.
              Persistent in localStorage. In BASIC mode we hide all
              secondary cards and keep the "3 essentials": daily log
              summary (insulin block), BHI CTA, and diary shortcut. */}
          <div className="flex items-center justify-end" data-testid="dashboard-view-mode-toggle">
            <div
              role="group"
              aria-label={bg ? "Изглед на таблото" : "Dashboard view"}
              className={`inline-flex rounded-full p-0.5 border text-[11px] font-semibold ${
                isDark
                  ? "bg-slate-900/60 border-slate-700"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <button
                type="button"
                onClick={() => setViewMode("basic")}
                data-testid="view-mode-basic"
                aria-pressed={isBasicView}
                className={`px-3 py-1 rounded-full transition ${
                  isBasicView
                    ? (isDark
                        ? "bg-emerald-500/30 text-emerald-200"
                        : "bg-emerald-100 text-emerald-800")
                    : (isDark ? "text-slate-400" : "text-slate-500")
                }`}
              >
                {bg ? "Основен" : "Basic"}
              </button>
              <button
                type="button"
                onClick={() => setViewMode("expert")}
                data-testid="view-mode-expert"
                aria-pressed={!isBasicView}
                className={`px-3 py-1 rounded-full transition ${
                  !isBasicView
                    ? (isDark
                        ? "bg-fuchsia-500/30 text-fuchsia-200"
                        : "bg-fuchsia-100 text-fuchsia-800")
                    : (isDark ? "text-slate-400" : "text-slate-500")
                }`}
              >
                {bg ? "Разширен" : "Expert"}
              </button>
            </div>
          </div>


          {/* iter1034 — Ред по заявка на потребителя: първо поздравът, после
              „Точка на истината", и чак след тях закачените и живите плочки.
              Преди поздрава не стои нищо. В основния изглед показваме лек
              поздрав (без score chrome), за да не започва екранът с плочки. */}
          {/* iter1076 — потребителят сам маха части от таб „Моят ден" */}
          {!isDesktop && (
            <ViewCustomizeBar
              isDark={isDark}
              editMode={mydayPrefs.editMode}
              setEditMode={mydayPrefs.setEditMode}
              hiddenCount={mydayPrefs.hidden.length}
              reset={mydayPrefs.reset}
              testid="myday-view-customize"
            />
          )}

          {isBasicView && <DashboardGreeting />}

          {/* iter1081 — „Един въпрос на първия екран": един отговор с думи,
              едно действие. Числата остават, но като второстепенни.
              Ползва вече заредените mealDilData + dilBudget (без нови заявки). */}
          <HideableSection id="myday-today-verdict" label="Как се отрази днешното хранене"
            prefs={mydayPrefs} isDark={isDark}>
            <TodayVerdictCard
              mealDilData={mealDilData}
              dilBudget={dilBudget}
              isDark={isDark}
              onLogMeal={() => {
                if (!isDesktop) {
                  setMobileSection("meals");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  navigate("/dashboard/diary");
                }
              }}
            />
          </HideableSection>

          {/* iter974 — Basic view hides LivePulse + Hero score. Expert
              users see the full hi-score + live-pulse chrome. */}
          {!isBasicView && (
            <>
              {/* iter586 — „Жив организъм" статус ред (фин пулс + час на синхронизация) */}
              <LivePulse lastSyncAt={lastSyncAt} change={liveChange} isDark={isDark} bg={bg} />

              {/* Block 1 — Hi Score Hero (now clickable → portal to HI) */}
              {/* iter614 — Hero Score Switch: HI / Twin / Both */}
              {(() => {
                const hsPref = user?.preferences?.hero_score || "hi";
                if (hsPref === "twin") {
                  return <TwinScoreHero API={API} isDark={isDark} bg={bg} />;
                }
                if (hsPref === "both") {
                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <HiScoreHero user={user} hiSummary={hiSummary} isDark={isDark} bg={bg} />
                      <TwinScoreHero API={API} isDark={isDark} bg={bg} />
                    </div>
                  );
                }
                return <HiScoreHero user={user} hiSummary={hiSummary} isDark={isDark} bg={bg} />;
              })()}
            </>
          )}

          {/* iter916 — Единна точка за качване (moved up from below LastAiReportCard).
              iter974 — Hidden in Basic view. Users can still upload from
              "Още" → Здраве & Анализи → Health Intelligence, or via the
              Diary shortcut when they scan a barcode/photo. */}
          {!isBasicView && <DashboardDocumentUploadCard isDark={isDark} />}
          {/* iter1017 — колко са попълнени „Моите данни" + просрочени изследвания */}
          <MyDataCompletenessStrip isDark={isDark} />

          {/* iter998 — Закачени плочки (до 4, drag-and-drop, запомнени в профила) */}
          <PinnedTilesRow bg={bg} />

          {/* iter1030 — четирите живи плочки в решетка, свити по подразбиране.
              Всяка се разгъва при клик и води към своя модул.
              iter1076 — на телефон целият ред е в сгъваема част, за да не
              товари отварянето на таблото. */}
          <HideableSection id="myday-live-tiles" label="Живите плочки на близнаците"
            prefs={mydayPrefs} isDark={isDark}>
          <AdaptiveGroup
            plain={isDesktop}
            title={bg ? "Живите плочки на близнаците" : "Live twin tiles"}
            icon={LayoutGrid}
            color="#8B5CF6"
            storageKey="myday-live-tiles"
            testId="mobile-fold-myday-tiles"
          >
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <DashboardWidget id="cortisol-intel">
              <CortisolIntelTile />
            </DashboardWidget>
            <DashboardWidget id="khi-intel">
              <TwinLiveTile twin="khi" />
            </DashboardWidget>
            <DashboardWidget id="bhi-intel">
              <TwinLiveTile twin="bhi" />
            </DashboardWidget>
            <DashboardWidget id="dhi-intel">
              <TwinLiveTile twin="dhi" />
            </DashboardWidget>
            <DashboardWidget id="hema-intel">
              <TwinLiveTile twin="hema" />
            </DashboardWidget>
            <DashboardWidget id="neuro-intel">
              <TwinLiveTile twin="neuro" />
            </DashboardWidget>
          </div>
          <Link to="/twins/network"
            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-violet-600 dark:text-violet-300"
            data-testid="dashboard-to-twin-network">
            Как се свързват близнаците — Жива физиологична мрежа
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </Link>
          </AdaptiveGroup>
          </HideableSection>

          {/* iter975 — Auto-graduation prompt for Basic users who have
              earned it (≥7 days registered + ≥5 meals logged). Nudges
              them into Expert view via micro-progression. */}
          <GraduateToExpertPrompt
            user={user}
            hiSummary={hiSummary}
            viewMode={viewMode}
            isDark={isDark}
            bg={bg}
            onGraduate={() => setViewMode("expert")}
          />

          {(() => {
            // iter577 — Welcome state за нови потребители (no meals, no labs, no body)
            const isNewUser = (
              (hiSummary?.metabolic?.meal_snapshots_count ?? 0) === 0 &&
              (hiSummary?.totals?.by_type?.lab_panel ?? 0) === 0 &&
              !user?.profile?.body?.weight
            );
            return isNewUser ? <WelcomeStateCard isDark={isDark} bg={bg} /> : null;
          })()}


          {/* iter920/iter972 — Auto-diagnosis panel moved BELOW the insulin
              budget block (Block 2) per user request. Was previously above
              Quick Actions; a screening summary makes more sense once the
              patient has seen today's metabolic KPIs. */}

          {/* iter1034 — ModuleShortcutTiles премахнати по заявка на потребителя
              (дублираха порталите и товареха екрана след „Точка на истината"). */}

          {/* iter912 — BHI preview + Daily Check-in removed from Dashboard.
              Digital Twin / living metabolism should not be diluted by
              compliance checklists or BHI drill on the home screen.
              Both remain reachable via left-nav (/pathway-vault, /bhi). */}

          {/* iter576 — Quick Actions row (4 friction-reducers).
              iter974 — Hidden in Basic view (patients can access all these
              actions via the Diary shortcut below the BHI CTA). */}
          {!isBasicView && (
            <QuickActionsRow
              isDark={isDark}
              bg={bg}
              API={API}
              user={user}
              onUserUpdated={refreshUser}
              onWeightSaved={() => {
                // iter582 — Cascade refresh so DIL budget + HI summary + Insulin
                // budget meter pick up the new weight immediately.
                fetchHiSummary();
                fetchMealDilData();
                setDilRefreshKey(k => k + 1);
              }}
            />
          )}

          {/* Block 2 — Insulin Budget + NIS Gauge + Today Intervention (iter586) */}
          <HideableSection id="myday-today-numbers" label="Днешните числа"
            prefs={mydayPrefs} isDark={isDark}>
          <AdaptiveGroup
            plain={isDesktop}
            title={bg ? "Днешните числа" : "Today's numbers"}
            icon={Activity}
            color="#0EA5E9"
            storageKey="myday-today-numbers"
            defaultOpen={true}
            testId="mobile-fold-myday-numbers"
          >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cc-row-budget-intervention">
            <InsulinBudgetMiniCard
              dailyData={dailyData}
              mealDilData={mealDilData}
              dilBudget={dilBudget}
              isDark={isDark}
              bg={bg}
              API={API}
              onDataChanged={() => {
                fetchDailyTracking();
                fetchMealDilData();
                setDilRefreshKey((k) => k + 1);
              }}
            />
            <NisGaugeCard mealDilData={mealDilData} isDark={isDark} bg={bg} />
            <TodayInterventionCard API={API} isDark={isDark} bg={bg} refreshSignal={lastSyncAt} onLogged={() => fetchDailyTracking()} mealDilData={mealDilData} dilBudget={dilBudget} />
          </div>

          {/* iter972 — Auto-diagnosis moved here (under Insulin Response).
              iter974 — Hidden in Basic view (only shown to Expert users
              who explicitly want the clinical detail layer). */}
          {!isBasicView && (
            <div className="mt-4">
              <AutoDiagnosisPanel lang={language || "bg"} />
            </div>
          )}
          </AdaptiveGroup>
          </HideableSection>

          {/* iter972 — Breast Health Intelligence CTA under Insulin Response.
              iter973 — Removed male-gender gate per user feedback: BHI is
              an educational + screening module useful for anyone caring
              for someone with breasts (partners, clinicians in training).
              The Male Digital Twin block already handles the reverse
              gating (hidden for women). */}
          <button
            type="button"
            onClick={() => navigate("/health-intelligence/breast")}
            data-testid="dashboard-bhi-cta"
            className={`w-full text-left rounded-2xl p-4 sm:p-5 border transition-all hover:shadow-md hover:-translate-y-0.5 group flex items-center gap-3 sm:gap-4 ${
              isDark
                ? "bg-gradient-to-br from-rose-950/40 via-[hsl(222,47%,10%)] to-pink-950/30 border-rose-800/40 hover:border-rose-500/60"
                : "bg-gradient-to-br from-rose-50 via-white to-pink-50 border-rose-200 hover:border-rose-400"
            }`}
          >
            <div
              className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl grid place-items-center ${
                isDark ? "bg-rose-500/20" : "bg-rose-100"
              }`}
            >
              <HeartPulseIcon
                className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? "text-rose-300" : "text-rose-600"}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm sm:text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Здраве на гърдата" : "Breast Health Intelligence"}
              </div>
              <div className={`text-[11px] sm:text-xs mt-0.5 ${isDark ? "text-rose-200/80" : "text-rose-700/90"}`}>
                {bg
                  ? "Персонализирана проверка и метаболитен профил за здравето на гърдите"
                  : "Personalised screening and metabolic profile for breast health"}
              </div>
            </div>
            <ChevronRightIcon
              className={`w-5 h-5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform ${
                isDark ? "text-rose-300" : "text-rose-600"
              }`}
            />
          </button>

          {/* iter974 — Basic-view diary shortcut. Sends the patient to the
              food-log flow in one tap — the third essential card next to
              Insulin block and BHI CTA. */}
          {isBasicView && (
            <button
              type="button"
              onClick={() => navigate("/dashboard/diary")}
              data-testid="dashboard-diary-shortcut"
              className={`w-full text-left rounded-2xl p-4 sm:p-5 border transition-all hover:shadow-md hover:-translate-y-0.5 group flex items-center gap-3 sm:gap-4 ${
                isDark
                  ? "bg-gradient-to-br from-emerald-950/40 via-[hsl(222,47%,10%)] to-teal-950/30 border-emerald-800/40 hover:border-emerald-500/60"
                  : "bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-emerald-200 hover:border-emerald-400"
              }`}
            >
              <div
                className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl grid place-items-center ${
                  isDark ? "bg-emerald-500/20" : "bg-emerald-100"
                }`}
              >
                <Utensils
                  className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? "text-emerald-300" : "text-emerald-600"}`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm sm:text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Хранителен дневник" : "Food diary"}
                </div>
                <div className={`text-[11px] sm:text-xs mt-0.5 ${isDark ? "text-emerald-200/80" : "text-emerald-700/90"}`}>
                  {bg
                    ? "Записвайте храненията и вижте MDI, SI и DIL"
                    : "Log food, see MDI/SI/DIL scores"}
                </div>
              </div>
              <ChevronRightIcon
                className={`w-5 h-5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform ${
                  isDark ? "text-emerald-300" : "text-emerald-600"
                }`}
              />
            </button>
          )}

          {/* iter974 — Everything below is EXPERT-only. Basic view keeps
              the dashboard to 3 essential cards (Insulin block + BHI CTA
              + Diary shortcut) so newly-registered patients aren't
              overwhelmed on day 1. */}
          {/* iter1067 — отбелязване „днес“: едно въвеждане храни няколко близнака */}
          <HideableSection id="myday-track-direction" label="Отбелязване и посока"
            prefs={mydayPrefs} isDark={isDark}>
          <AdaptiveGroup
            plain={isDesktop}
            title={bg ? "Отбелязване и посока" : "Track & trend"}
            icon={HeartPulseIcon}
            color="#EC4899"
            storageKey="myday-track-direction"
            testId="mobile-fold-myday-track"
          >
          <div className="space-y-5">
          <DailyVitalsTile />

          {/* iter1071 — посоката на храненето с бърз поглед */}
          <PlateTrendChart compact />

          {/* iter1066 — Моите цели за седмицата (видими и в основния изглед) */}
          <WeeklyGoalsCard />
          </div>
          </AdaptiveGroup>
          </HideableSection>

          {!isBasicView && (
            <HideableSection id="myday-advanced" label="Разширени раздели"
              prefs={mydayPrefs} isDark={isDark}>
            <AdaptiveGroup
              plain={isDesktop}
              title={bg ? "Разширени раздели" : "Advanced sections"}
              icon={Sparkles}
              color="#F59E0B"
              storageKey="myday-advanced"
              testId="mobile-fold-myday-advanced"
            >
            <div className="space-y-5">
          {/* iter576 — Block 2.5 — Днешен фокус (dynamic single-action recommendation) */}
          <DailyFocusCard user={user} mealDilData={mealDilData} dilBudget={dilBudget} hiSummary={hiSummary} isDark={isDark} bg={bg} API={API} />


          {/* iter619 — Passive stress detection banner (auto-hides when no triggers) */}
          <PassiveStressBanner />

          {/* iter610 — Unified Health Twin Card (Twin Score + 5 близнака) — desktop visible */}
          <UnifiedHealthTwinCard />

          {/* iter1030 — „Мъжки Health Twin" е изнесен от таблото (когнитивно
              претоварване); достъпен е на /dashboard/male-health-twin и от
              лентата „Свързани модули" в Health Intelligence. */}

          {/* Block 3 — Attention Needed */}
          <AttentionNeededCard user={user} dailyData={dailyData} hiSummary={hiSummary} isDark={isDark} bg={bg} />

          {/* iter576 — Section: ОСНОВНИ МОДУЛИ */}
          <div className="pt-2">
            <div className={`text-[11px] uppercase tracking-[0.14em] font-bold mb-2 px-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              {bg ? "Основни раздели" : "Core modules"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cc-portals-row-1">
              <ModulePortalLive
                emoji="🍽"
                title="Nutrition Lab"
                signals={(() => {
                  const dilAvg = hiSummary?.metabolic?.dil_avg;
                  const meals30 = hiSummary?.metabolic?.meal_snapshots_count ?? 0;
                  const used = Math.round(((mealDilData?.daily_effective_dil ?? 0) * 10) / 10);
                  const cap = dilBudget || 97;
                  return [
                    bg
                      ? (typeof dilAvg === "number" && dilAvg < 50 ? `Среден DIL ↓ спокоен (${dilAvg.toFixed(1)})` : `Днес: ${used} / ${cap}`)
                      : `Today: ${used} / ${cap}`,
                    bg
                      ? (meals30 >= 7 ? "Стабилен ритъм на записване" : "Запиши още — за по-точен модел")
                      : (meals30 >= 7 ? "Steady logging rhythm" : "Log more — sharpen model"),
                  ];
                })()}
                to="/dashboard/nutrition-lab"
                accent="emerald"
                isDark={isDark}
                testId="cc-portal-nutrition"
              />
              <ModulePortalLive
                emoji="🧠"
                title="Mind & Body Lab"
                signals={(() => {
                  const interventions = hiSummary?.totals?.by_type?.lifestyle_intervention ?? 0;
                  return [
                    bg ? (interventions >= 3 ? `Активни ритуали: ${interventions}` : "Стартирай първи ритуал") : (interventions >= 3 ? `Active rituals: ${interventions}` : "Start your first ritual"),
                    bg ? "30-мин разходка препоръчана днес" : "30-min walk recommended today",
                  ];
                })()}
                to="/dashboard/mindbody-lab"
                accent="violet"
                isDark={isDark}
                testId="cc-portal-mindbody"
              />
              <ModulePortalLive
                emoji="🔬"
                title={bg ? "Прецизно здраве" : "Precision Health"}
                signals={(() => {
                  const labs = hiSummary?.totals?.by_type?.lab_panel ?? 0;
                  return [
                    bg ? (labs > 0 ? `${labs} анализа готови за разглеждане` : "Качи първи анализ → +12% точност") : (labs > 0 ? `${labs} analyses ready to view` : "Upload first lab → +12% accuracy"),
                    bg ? "Биомаркери в реално време" : "Real-time biomarkers",
                  ];
                })()}
                to="/dashboard/analyses"
                accent="sky"
                isDark={isDark}
                testId="cc-portal-precision"
              />
            </div>
          </div>

          {/* iter576 — Section: РАЗШИРЕНИ МОДУЛИ. iter972 hides on mobile. */}
          <div className="hidden md:block pt-2">
            <div className={`text-[11px] uppercase tracking-[0.14em] font-bold mb-2 px-1 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
              {bg ? "Разширени раздели" : "Advanced modules"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cc-portals-row-2">
              <ModulePortalLive
                emoji="🧬"
                title={bg ? "Дигитален близнак" : "Digital Twin"}
                subtitle={bg ? "Симулации в Хранителен дневник" : "In your Diary"}
                signals={[
                  bg ? `Симулации: ${hiSummary?.totals?.by_type?.digital_twin ?? 0}` : `Simulations: ${hiSummary?.totals?.by_type?.digital_twin ?? 0}`,
                  bg ? "Стартирай нова →" : "Run new →",
                ]}
                to="/dashboard/diary"
                accent="pink"
                isDark={isDark}
                testId="cc-portal-twin"
              />
              <ModulePortalLive
                emoji="🩺"
                title={bg ? "Здраве на гърдите и зъбите" : "Breast & Dental Health"}
                subtitle={bg ? "Кратка проверка + помощ от AI" : "Screening + AI support"}
                signals={[
                  bg ? "Ранно откриване и превенция" : "Early detection & prevention",
                  bg ? "Виж сигналите →" : "View signals →",
                ]}
                to="/dashboard/health-intelligence"
                accent="amber"
                isDark={isDark}
                testId="cc-portal-organ"
              />
              <ModulePortalLive
                emoji="🤖"
                title={bg ? "AI Специалисти" : "AI Specialists"}
                subtitle={bg ? "В „Прецизно здраве“" : "In Precision Health"}
                signals={[
                  "EndoBot · NutriBot · HemoBot",
                  bg ? "Попитай за нещо →" : "Ask anything →",
                ]}
                to="/dashboard/analyses"
                accent="rose"
                isDark={isDark}
                testId="cc-portal-ai"
              />
            </div>
          </div>

          {/* iter1030 — „Вашата здравна екосистема" е скрита от таблото —
              същата карта живее в Health Hub (без дублиране). */}

          {/* iter696 NOTE: 3D Digital Twin отложен поради r3f 9.x + React 19 + CRA
              babel jsx-source incompatibility. Component file `DigitalTwin3D.jsx`
              готов и работещ; нужна е babel конфигурация в iter697. */}

          {/* iter692 — Smart Lab Upload Nudge (auto-hides at 90% completeness).
              iter972 — Hidden on mobile: duplicates the DashboardDocumentUploadCard
              above (both prompt for lab-doc upload). Retained on desktop. */}
          <div className="hidden md:block">
            <LabAggregateNudgeCard isDark={isDark} bg={bg} />
          </div>

          {/* iter1030 — „Моите снимки на здравето" е скрита от таблото;
              снимките си остават в профила и в модула за гърдата. */}

          {/* iter577 — Health Journey: temporal milestones */}
          <HealthJourneyCard user={user} hiSummary={hiSummary} isDark={isDark} bg={bg} />

          {/* Feb 2026 — Последен AI доклад (from onboarding / rerun wizard).
              Показва teaser + BMR/TDEE/protein/carbs + "Виж пълния анализ".
              Auto-hides ако user още не е генерирал доклад. */}
          <LastAiReportCard isDark={isDark} bg={bg} />

          {/* iter916 — DashboardDocumentUploadCard moved higher (right after
              Twin Score hero) so patients see where diagnoses come from. */}

          {/* Block 6 — Recent Changes */}
          <RecentChangesCard hiSummary={hiSummary} isDark={isDark} bg={bg} />
            </div>
            </AdaptiveGroup>
            </HideableSection>
          )}

          {/* iter977 — Compact "What's new" release-notes strip at the
              bottom of the myday scroll area. Version-scoped dismissal
              so returning users notice each release. */}
          <ReleaseNotesFooter isDark={isDark} bg={bg} />

          {/* Hidden mounts — запазват event handlers, не се рендира визуално */}
          <div className="sr-only" aria-hidden="true">
            <MealLog onMealsUpdate={() => { fetchDailyTracking(); fetchWeeklyData(); fetchMealDilData(); setDilRefreshKey(k => k + 1); }} selectedDate={trackingDate} openSignal={mealLogOpenSignal} />
          </div>
          <AIRecommendationPopup result={result} onMealsAdded={() => { fetchDailyTracking(); fetchWeeklyData(); setDilRefreshKey(k => k + 1); }} />
          {activePanel === "history" && <HistoryPanel history={history} onSelect={handleHistoryClick} />}
        </div>
        )}

        {/* Mobile: Section-based layout (mount-нат само на <md viewports) */}
        {!isDesktop && (
        <>
          <MobileScrollProgress />
        <div className="mt-3 space-y-3">

          {/* Meals section — iter313 restructure (user request):
              Подредба:
              1. Insight карти (profile, onboarding, daily insight, next step)
              2. AI анализ на храна (FoodImageAnalyzer)
              3. Снимка-рецепта (RecipeVisionUpload — beше скрита в MealLog)
              4. Симулатор на чинията (PlateSimulator)
              5. Digital Twin
              6. Умно хранене (MealIntelligence)
              7. AI Meal Planner (MealPlanner)
              8. Преглед на хранителен режим (MealPlanReview)
              Премахнати: MealLog, MealHistoryPanel, SymptomTrackerWidget,
              MindBodyRecommender (преместен в Анализи tab).
          */}
          {mobileSection === "meals" && (
            <div className="space-y-3 animate-fade-in-up">
              {/* iter1073 — потребителят сам маха секции от таб „Хранене" */}
              <ViewCustomizeBar
                isDark={isDark}
                editMode={mealPrefs.editMode}
                setEditMode={mealPrefs.setEditMode}
                hiddenCount={mealPrefs.hidden.length}
                reset={mealPrefs.reset}
                testid="meals-view-customize"
              />
              {/* iter1076 — „Както вчера": едно докосване записва последния
                  ден с хранения в днешния дневник. */}
              <HideableSection id="meals-repeat-yesterday" label="Както вчера"
                prefs={mealPrefs} isDark={isDark}>
                <RepeatYesterdayCard
                  onCopied={() => { fetchDailyTracking(); fetchWeeklyData(); fetchMealDilData(); setDilRefreshKey(k => k + 1); }}
                />
              </HideableSection>
              {/* iter380 — MealsActionGrid moved to TOP of dashboard render
                  (before hero/composite/overview) so patient lands on tools.
                  See block above this mobileSection ladder. */}

              {/* 1. Разчитане на храна по снимка */}
              <HideableSection id="meals-food-analyzer" label="Разчитане на храна по снимка"
                prefs={mealPrefs} isDark={isDark}>
                <div id="dashboard-food-analyzer-anchor">
                  <FoodImageAnalyzer onAddCalories={handleAddFromFoodAnalysis} />
                </div>
              </HideableSection>

              {/* 2. Снимка-рецепта — отворен по default за бърз достъп */}
              <HideableSection id="meals-recipe-photo" label="Снимка на рецепта"
                prefs={mealPrefs} isDark={isDark}>
              <MobileFoldSection
                title={bg ? "Снимка на рецепта" : "Recipe photo"}
                icon={CameraIcon}
                color="#0EA5E9"
                storageKey="recipe-vision"
                defaultOpen={true}
                testId="mobile-fold-recipe-vision"
              >
                <RecipeVisionUpload
                  variant="meal-log"
                  onAdded={(meal) => { fetchDailyTracking(); fetchWeeklyData(); fetchMealDilData(); setDilRefreshKey(k => k + 1); window.dispatchEvent(new CustomEvent("meal-logged", { detail: { nis_pct: meal?.nis_pct ?? meal?.dil ?? null, meal_id: meal?.id, source: "recipe-vision" } })); window.dispatchEvent(new Event("meal-added")); }}
                />
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/recipes")}
                  className="mt-2 w-full text-xs font-semibold rounded-full px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5"
                  data-testid="dashboard-recipe-vault-link"
                >
                  📖 {bg ? "Готварска книга (всички запазени)" : "Recipe Vault (all saved)"}
                </button>
              </MobileFoldSection>
              </HideableSection>

              {/* iter1071 — как се движи чинията по дни */}
              <HideableSection id="meals-plate-trend" label="Как се движи чинията по дни"
                prefs={mealPrefs} isDark={isDark}>
                <div id="plate-trend-anchor" className="mb-4">
                  <PlateTrendChart days={30} />
                </div>
              </HideableSection>

              {/* 3. Симулатор на чинията — отворен по default */}
              <HideableSection id="meals-plate-simulator" label="Симулатор на чинията"
                prefs={mealPrefs} isDark={isDark}>
              <div id="plate-simulator-anchor">
                <MobileFoldSection
                  title={bg ? "Симулатор на чинията" : "Plate Simulator"}
                  icon={Utensils}
                  color="#10B981"
                  storageKey="plate-sim"
                  defaultOpen={true}
                  testId="mobile-fold-plate-simulator"
                >
                  <div className="space-y-2">
                    <PlateSimulator />
                  </div>
                </MobileFoldSection>
              </div>
              </HideableSection>

              {/* iter972 — Removed from mobile "Хранене" per user request:
                    • "Преглед на деня" (daily overview fold)
                    • "Digital Twin — Foundation" (Cortisol + Sleep + Memory)
                    • "Ендокринен профил" (dynamic phenotype signature)
                  All 3 remain visible on desktop and are reachable from
                  the "Още" (More) tab (see mobileSection === "more"). */}

              {/* iter1073 — „Симулирай в дигиталния близнак" е премахнат от таб
                  „Хранене" по заявка на потребителя. Разделът си остава в
                  хранителния дневник. */}
              {/* 5. Умно хранене — collapsed by default */}
              <HideableSection id="meals-smart-nutrition" label="Умно хранене"
                prefs={mealPrefs} isDark={isDark}>
              <MobileFoldSection
                title={bg ? "Умно хранене" : "Smart Nutrition"}
                icon={Sparkles}
                color="#F59E0B"
                storageKey="meal-intelligence"
                testId="mobile-fold-meal-intelligence"
              >
                <MealIntelligence refreshKey={dilRefreshKey} onMealAdded={(meal) => { fetchDailyTracking(); fetchWeeklyData(); setDilRefreshKey(k => k + 1); window.dispatchEvent(new CustomEvent("meal-logged", { detail: { nis_pct: meal?.nis_pct ?? meal?.dil ?? null, meal_id: meal?.id, source: "meal-intelligence" } })); window.dispatchEvent(new Event("meal-added")); }} />
              </MobileFoldSection>
              </HideableSection>
              {/* iter316 — AI Meal Planner и Преглед на хранителен режим преместени
                  в Хранителен дневник (DiaryPage) под Анализ на дефицити. */}
              {/* PathwayCoverageCard и MealComplianceWidget (Phenotype Pathway Picks)
                  преместени в Здраве tab (iter327X — потребителска заявка) */}
              {/* iter351.G — "Хранителен дневник" UI скрит от patient Hranenie tab,
                  но MealLog остава mount-нат (visually-hidden) защото бутоните
                  „Добави храна" и AI симулация в PlateSimulator/DigitalTwin
                  dispatch-ват `meal-log:focus-search` event и изискват MealLog да е bound. */}
              {/* iter1073 — дневникът вече се ВИЖДА на таб „Хранене".
                  Дотук стоеше скрит (`sr-only`), затова натиснатото
                  „Добавяне на храна" отваряше търсачка, която потребителят не
                  виждаше, и добавената храна не се появяваше никъде. */}
              <HideableSection id="meals-meal-log" label="Какво изядох днес"
                prefs={mealPrefs} isDark={isDark}>
                <div id="meal-log-anchor">
                  <MobileFoldSection
                    title={bg ? "Какво изядох днес" : "What I ate today"}
                    icon={BookOpen}
                    color="#EC4899"
                    storageKey="mobile-meallog"
                    defaultOpen={true}
                    testId="mobile-fold-meallog"
                  >
                    <MealLog
                      onMealsUpdate={() => { fetchDailyTracking(); fetchWeeklyData(); fetchMealDilData(); setDilRefreshKey(k => k + 1); }}
                      selectedDate={trackingDate}
                      openSignal={mealLogOpenSignal}
                    />
                  </MobileFoldSection>
                </div>
              </HideableSection>
              {/* iter1073 — „Оптимизатор на времето за хранене" е премахнат от
                  таб „Хранене" по заявка на потребителя. */}
            </div>
          )}

          {/* iter327Z — НОВ split (по заявка):
              "Моето здраве" (mobile-section-health):
                Daily tracking, MetabolicPanel ("Как работи тялото ви"),
                Dietary Patterns, Deficiency Analysis, Health Indices.
              "Анализи" (mobile-section-ai):
                Lab Upload, Medical Imaging, Health Calculators, Dental Risk,
                Breast Cancer Risk (clinician-only), Longevity, Bio Age,
                Clinical Analysis, Ask AI.
              Phenotype Pathway Picks (MealComplianceWidget) и Pathway Coverage —
                clinician-only (доктор акаунт).
          */}
          {mobileSection === "health" && (
            <div className="space-y-3" data-testid="mobile-section-health">
              {/* iter603 — Unified Health Twin (3-twin command center) */}
              <UnifiedHealthTwinCard />
              <DailyTracking dailyData={dailyData} goals={goals} onUpdate={updateDailyData} selectedDate={trackingDate} onDateChange={handleDateChange} />
              {/* Carousel: "Как работи тялото ви" + "Хранителни модели" + "Анализ на дефицити" */}
              {/* iter351.G — Sized skeleton fallback за MetabolicPanel в carousel
                  предотвратява "появяване изведнъж" след lazy chunk load. */}
              <AnalysesCarousel
                slides={[
                  { key: "metabolic", node: (
                    <Suspense fallback={
                      <div className={`rounded-2xl border min-h-[420px] flex items-center justify-center ${isDark ? "bg-[hsl(223,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"}`}>
                        <div className="flex flex-col items-center gap-2 opacity-70">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] animate-pulse" />
                          <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            {bg ? "Зареждам метаболитното състояние…" : "Loading metabolic status…"}
                          </p>
                        </div>
                      </div>
                    }>
                      <MetabolicPanel key={dilRefreshKey} selectedDate={trackingDate} />
                    </Suspense>
                  ) },
                  { key: "patterns", node: <DietaryPatternsCard /> },
                  { key: "deficiency", node: <NutrientDeficiencyPanel /> },
                ]}
              />
              {/* HealthIndices — full-width извън carousel заради overflow на високо съдържание */}
              <HealthIndices />
              {/* iter351.E: PathwayCoverageCard преместен в MD таб (Coverage phenotypes).
                  MealComplianceWidget остава тук — patient-facing daily meal check. */}
              {isClinician && (
                <MealComplianceWidget refreshKey={dilRefreshKey} onMealAdded={() => { fetchDailyTracking(); fetchWeeklyData(); fetchMealDilData(); setDilRefreshKey(k => k + 1); window.dispatchEvent(new Event("meal-added")); }} />
              )}
            </div>
          )}

          {mobileSection === "ai" && (
            <div className="space-y-3" data-testid="mobile-section-ai">
              {/* Medical / clinical tools — patient-facing analyses */}
              <LabUploadAnalyzer />
              <MedicalImageScreening />
              <HealthCalculators onGoalsUpdated={() => { fetchDailyTracking(); }} />
              <DentalRiskTile />
              <LongevityScorePanel />
              <BiologicalAgePanel />
              {/* Попитай AI (ChatInterface) */}
              <ChatInterface query={query} setQuery={setQuery} onSubmit={handleAskAI} loading={loading} result={result} />
              {result?.final_response && <AIInsightsPanel result={result} />}
              <UpgradeNudge />
              <ChatHistoryList
                history={chatHistory}
                onSelect={(entry) => { setQuery(entry.query); setResult(entry.result); }}
                onRemove={removeChatEntry}
                onClear={clearChatHistory}
              />
              <AIRecommendationPopup result={result} onMealsAdded={() => { fetchDailyTracking(); fetchWeeklyData(); setDilRefreshKey(k => k + 1); }} />
              {history?.length > 0 && <HistoryPanel history={history} onSelect={handleHistoryClick} />}
            </div>
          )}

          {/* iter351.E: MD section — clinician-only deep clinical modules.
              Tab visible само за approved clinicians (isClinician=true).
              Съдържа: Клиничен анализ, Клинични фенотипи, Coverage phenotypes,
              Mind-Body Interventions, Епигенетична карта. Допълнително: Breast
              Cancer Risk и Skin Lesion screening — преместени от Анализи. */}
          {mobileSection === "md" && isClinician && (
            <div className="space-y-3" data-testid="mobile-section-md">
              {/* Header banner — MD context cue */}
              <div className={`rounded-2xl p-4 ${isDark ? "bg-gradient-to-br from-emerald-900/40 to-teal-900/30 border border-emerald-700/30" : "bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white text-lg">
                    🩺
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "MD — Клинични инструменти" : "MD — Clinical tools"}
                    </p>
                    <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {bg ? "Достъп само за лекари (клиницисти)" : "Clinician-only access"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Клиничен анализ (gene-based AI insight) */}
              <ClinicalAnalysis onStartChat={handleStartChatFromGene} />

              {/* Клинични фенотипи (MDR Class IIa — clinician-only) */}
              {sessionStorage.getItem("nutramilo_composite_phenotype_visible") !== "1" && (
                canClinicalPhenotypes ? <PhenotypeClassifier /> : <UpgradeLock feature="clinical_phenotypes" />
              )}

              {/* Coverage phenotypes — Pathway Coverage за phenotype-driven nutrient gaps */}
              <PathwayCoverageCard refreshKey={dilRefreshKey} date={trackingDate} />

              {/* Mind-Body Interventions */}
              <MindBodyRecommender refreshKey={dilRefreshKey} />

              {/* Епигенетична карта (clinician-gated) */}
              {canClinicalPhenotypes ? <EpigeneticMapPanel /> : <UpgradeLock feature="epigenetic_map" />}

              {/* iter538 — Breast Cancer Risk Panel removed from clinical
                  Precision Health menu (MDR Rule 11 exposure). 
                  remains accessible via the public educational /breast-health
                  page (BreastHealthHub) which is properly disclaimer-framed. */}

              {/* Skin lesion screening — full page route */}
              <button
                type="button"
                onClick={() => navigate("/skin-screening")}
                className={`w-full text-left p-4 rounded-2xl border transition-all ${
                  isDark
                    ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)] hover:border-pink-500/50"
                    : "bg-white border-gray-100 shadow-sm hover:shadow-md"
                }`}
                data-testid="md-tile-skin-lesion"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white text-lg">
                    🩻
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Кожни образувания (ABCDE)" : "Skin lesion screening (ABCDE)"}
                    </p>
                    <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {bg ? "Образователен преглед · не е диагноза" : "Educational review · not a diagnosis"}
                    </p>
                  </div>
                  <span className={`text-lg ${isDark ? "text-gray-500" : "text-gray-300"}`}>→</span>
                </div>
              </button>
            </div>
          )}

          {/* More section — iter972 curated drawer.
              Two grouped sections per designer proposal:
                1. Здраве & Анализи — Precision Health, Auto-diagnoses,
                   Ендокринен профил, Snapshots, Разширени модули
                2. Настройки — App prefs, security, export, feedback, trial */}
          {mobileSection === "more" && (
            <div className="space-y-4 pb-4" data-testid="mobile-section-more">
              {/* Group 1: Здраве & Анализи */}
              <div>
                <div className={`text-[10.5px] uppercase tracking-[0.14em] font-bold mb-2 px-1 ${isDark ? "text-sky-300" : "text-sky-700"}`}>
                  {bg ? "Здраве & Анализи" : "Health & Analyses"}
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/analyses")}
                    data-testid="more-tile-precision"
                    className={`text-left rounded-2xl p-3.5 border transition ${isDark ? "bg-slate-900/50 border-slate-700 hover:border-sky-500/50" : "bg-white border-gray-200 hover:border-sky-400 hover:shadow-sm"}`}
                  >
                    <div className="text-2xl">📊</div>
                    <div className={`text-xs font-bold mt-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Прецизно здраве" : "Precision Health"}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {bg ? "Дълбоки лабораторни анализи" : "Deep lab analyses"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/health-intelligence")}
                    data-testid="more-tile-health-intel"
                    className={`text-left rounded-2xl p-3.5 border transition ${isDark ? "bg-slate-900/50 border-slate-700 hover:border-rose-500/50" : "bg-white border-gray-200 hover:border-rose-400 hover:shadow-sm"}`}
                  >
                    <div className="text-2xl">🩺</div>
                    <div className={`text-xs font-bold mt-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Здравни раздели" : "Health Intelligence"}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {bg ? "Здраве на гърдата + раздели за проверка" : "BHI + screening modules"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileSection("ai")}
                    data-testid="more-tile-auto-dx"
                    className={`text-left rounded-2xl p-3.5 border transition ${isDark ? "bg-slate-900/50 border-slate-700 hover:border-emerald-500/50" : "bg-white border-gray-200 hover:border-emerald-400 hover:shadow-sm"}`}
                  >
                    <div className="text-2xl">🧬</div>
                    <div className={`text-xs font-bold mt-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "AI Специалисти" : "AI Specialists"}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      EndoBot · NutriBot · HemoBot
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/diary")}
                    data-testid="more-tile-diary"
                    className={`text-left rounded-2xl p-3.5 border transition ${isDark ? "bg-slate-900/50 border-slate-700 hover:border-violet-500/50" : "bg-white border-gray-200 hover:border-violet-400 hover:shadow-sm"}`}
                  >
                    <div className="text-2xl">📔</div>
                    <div className={`text-xs font-bold mt-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {bg ? "Дневник" : "Diary"}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {bg ? "Хронология + AI преглед" : "Timeline + AI review"}
                    </div>
                  </button>
                </div>
              </div>

              {/* Group 2: Настройки */}
              <div>
                <div className={`text-[10.5px] uppercase tracking-[0.14em] font-bold mb-2 px-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {bg ? "Настройки & Утилити" : "Settings & Utilities"}
                </div>
                <div className="space-y-3">
                  <AppPreferencesPanel />
                  <SecuritySettings />
                  <FeedbackPanel />
                  <ReviewPromptModal />
                  <TrialDashboard />
                  <ExportPanel />
                </div>
              </div>
            </div>
          )}
        </div>
        </>
        )}
        </Suspense>
      </main>

      {/* iter561 — Mandatory regulatory disclaimer (EU MDR framing) */}
      <RegulatoryFooter />
      <BCSubtypeOnboardingPrompt />
      <ScreenRecorderWidget />

      {isDesktop && (
      <div className="block">
        <AppFooter compact hideCompliance />
      </div>
      )}
      <MobileBottomNav
        activeSection={mobileSection}
        onSectionChange={(id) => {
          // iter972 — Recipes and Profile navigate to dedicated pages so we
          // don't have to duplicate their layouts inside the Dashboard.
          if (id === "recipes") {
            navigate("/dashboard/recipes");
            return;
          }
          if (id === "profile") {
            navigate("/dashboard/profile");
            return;
          }
          setMobileSection(id);
        }}
        isClinician={isClinician}
      />
      <ProfileEditModal open={showProfileEdit} onClose={() => setShowProfileEdit(false)} />
      <ComplianceConsentModal />
      <PwaInstallPrompt />
      <ScrollToTop />
      {/* iter686 — GuidedTour disabled (11-step walkthrough obsolete after
          добавянето на 18-те близнаци). Replaced by ModuleShortcutTiles above. */}
      {/* <GuidedTour /> */}
      {/* iter350.D — VoiceLogFab премахнат от Dashboard по потребителско искане
          (конфликтуваше с DashboardGuide button в долния ляв ъгъл).
          Voice logging остава достъпен директно от MealLog: компонентът
          MealLogVoiceButton е вграден в дневника.
          iter538 — On mobile ONLY (Хранене tab), mount a headless VoiceLogFab
          + BarcodeScannerModal so the MealsActionGrid "Гласов лог" / "Скенирай
          баркод" tiles have working listeners. VoiceLogFab already listens
          for the `voice-meal-trigger` event (same one PlateSimulator uses)
          and BarcodeScannerModal listens for `barcode-scanner:open`. */}
      {!isDesktop && mobileSection === "meals" && (
        <Suspense fallback={null}>
          <VoiceLogFab hideFab />
          <BarcodeScannerModal />
        </Suspense>
      )}

      {/* More Drawer — Messages / Weekly Review / Weekly Progress / Recipes / Referrals */}
      <MoreDrawer
        open={moreDrawer !== null}
        title={
          moreDrawer === "inbox" ? (bg ? "Съобщения" : "Messages") :
          moreDrawer === "weekly" ? (bg ? "Седмичен преглед" : "Weekly Review") :
          moreDrawer === "progress" ? (bg ? "Седмичен напредък" : "Weekly Progress") :
          moreDrawer === "recipes" ? (bg ? "Рецепти" : "Recipes") :
          moreDrawer === "referral" ? (bg ? "Реферална програма" : "Referrals") : ""
        }
        onClose={() => setMoreDrawer(null)}
      >
        <Suspense fallback={null}>
          {moreDrawer === "inbox" && <InboxPanel />}
          {moreDrawer === "weekly" && <WeeklyReport />}
          {moreDrawer === "progress" && <WeeklyChart weeklyData={weeklyData} goals={goals} />}
          {moreDrawer === "recipes" && <RecipeExplorer />}
          {moreDrawer === "referral" && <ReferralPanel />}
        </Suspense>
      </MoreDrawer>
      {/* iter592 — DashboardGuide „Ръководство" бутон премахнат по user request */}
    </div>
  );
}
